import { Alert, Box, Button, Typography } from '@mui/material'
import type { ContentProposal } from '@shared/contentProposal'
import React, { useRef, useState } from 'react'

import type { ApplyProposalAction } from './ContentProposalReview'
import { type ProposalBatchResult, decideContentProposals } from '../../../services/contentProposals'

export default function ContentProposalBulkActions({
  bookId,
  filename,
  proposals,
  onApply,
}: {
  bookId: string
  filename: string
  proposals: ContentProposal[]
  onApply: ApplyProposalAction
}) {
  const [busy, setBusy] = useState(false)
  const running = useRef(false)
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'warning' | 'error' } | null>(null)
  const pending = proposals.filter((item) => item.filename === filename && item.status === 'pending')
  const applying = proposals.some((item) => item.status === 'applying')
  const act = async (action: 'accept' | 'reject') => {
    if (running.current || applying || !pending.length) {
      return
    }
    running.current = true
    setBusy(true)
    setMessage(null)
    // Capture the displayed versions: newly arriving proposals are not silently included.
    const selected = [...pending]
    try {
      let result: ProposalBatchResult | undefined
      const execute = async () => {
        result = await decideContentProposals(bookId, selected, action)
      }
      if (action === 'accept') {
        await onApply(execute, filename)
      } else {
        await execute()
      }
      const { completed, failures } = result!
      const done = action === 'accept' ? 'אושרו' : 'נדחו'
      setMessage(
        failures.length
          ? { severity: 'warning', text: `${done}: ${completed}. לא טופלו: ${failures.length}. ${failures[0].message}` }
          : {
              severity: 'success',
              text: action === 'accept' ? 'כל ההצעות שנבחרו בקובץ אושרו.' : 'כל ההצעות שנבחרו בקובץ נדחו.',
            },
      )
    } catch (error) {
      setMessage({ severity: 'error', text: error instanceof Error ? error.message : 'עדכון ההצעות נכשל' })
    } finally {
      running.current = false
      setBusy(false)
    }
  }
  if (!pending.length && !applying && !message && !busy) {
    return null
  }
  return (
    <Box
      data-testid="proposal-bulk-actions"
      dir="rtl"
      sx={{ borderBottom: '1px solid var(--toolbar-page-border)', px: 1.5, py: 0.5 }}
    >
      {(pending.length > 0 || applying || busy) && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant="caption" sx={{ flexGrow: 1 }}>
            הצעות ב״{filename}״
          </Typography>
          <Button
            size="small"
            disabled={busy || applying || !pending.some((item) => item.canApply)}
            onClick={() => void act('accept')}
          >
            אשר הכל
          </Button>
          <Button size="small" disabled={busy || applying || !pending.length} onClick={() => void act('reject')}>
            דחה הכל
          </Button>
        </Box>
      )}
      {message && (
        <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ my: 0.5 }}>
          {message.text}
        </Alert>
      )}
    </Box>
  )
}
